import React, { useState, useEffect, useCallback } from "react";
import Sidebar from "./Navbar";
import { BASE_URL } from "./constants";

const Settings = () => {
  const admin = JSON.parse(localStorage.getItem("admin") || "{}");

  const [activeTab, setActiveTab] = useState("financial"); // financial | dispatch | support | security
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Settings State
  const [settings, setSettings] = useState({
    financial: {
      commissionRate: 10,
      maxOutstandingCashDues: 500,
      minWithdrawalAmount: 100,
      maxWithdrawalAmount: 25000,
      referralBonus: 10,
      gstRate: 18,
    },
    dispatch: {
      bookingExpiryMinutes: 3,
      maxSearchRadiusKm: 15,
      autoCutEnabled: true,
    },
    support: {
      supportPhone: "+91 98765 43210",
      supportWhatsapp: "+91 98765 43210",
      supportEmail: "support@inb.com",
      operatingHours: "08:00 AM - 10:00 PM IST",
    },
    system: {
      platformName: "INB - My Buddy",
      environment: "Production",
      maintenanceMode: false,
    },
    appVersion: {
      latestVersion: "1.0.0",
      forceUpdate: false,
      storeUrl: "https://play.google.com/store/apps/details?id=com.inbuser&hl=en_IN",
    },
    providerAppVersion: {
      latestVersion: "1.0.0",
      forceUpdate: false,
      storeUrl: "https://play.google.com/store/apps/details?id=com.aloneking6798.inbprovider",
    },
  });

  const [adminProfile, setAdminProfile] = useState(null);
  const [diagnostics, setDiagnostics] = useState({
    database: "CONNECTED",
    redis: "CONNECTED",
    nodeVersion: "v24",
    uptimeSeconds: 0,
    memoryUsageMB: 0,
    serverTimestamp: "",
  });

  // Password Change Form State
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordSaving, setPasswordSaving] = useState(false);

  // Toast Notification
  const [toast, setToast] = useState(null);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("admin");
    window.location.href = "/login";
  };

  // Fetch Settings from Backend
  const fetchSettings = useCallback(async (isManual = false) => {
    if (isManual) setRefreshing(true);
    else setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${BASE_URL}admin/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();

      if (json.status === "success" && json.data) {
        if (json.data.settings) setSettings(json.data.settings);
        if (json.data.adminProfile) setAdminProfile(json.data.adminProfile);
        if (json.data.diagnostics) setDiagnostics(json.data.diagnostics);
      } else {
        showToast(json.message || "Failed to load platform settings", "error");
      }
    } catch (err) {
      console.error("Error fetching settings:", err);
      showToast("Network error fetching settings", "error");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Handle Save Settings
  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");

      const payload = {
        ...settings,
        financial: {
          ...settings.financial,
          commissionRate: settings.financial?.commissionRate === "" ? 0 : Number(settings.financial?.commissionRate ?? 0),
          maxOutstandingCashDues: settings.financial?.maxOutstandingCashDues === "" ? 500 : Number(settings.financial?.maxOutstandingCashDues ?? 500),
          referralBonus: settings.financial?.referralBonus === "" ? 0 : Number(settings.financial?.referralBonus ?? 0),
          minWithdrawalAmount: settings.financial?.minWithdrawalAmount === "" ? 0 : Number(settings.financial?.minWithdrawalAmount ?? 0),
          maxWithdrawalAmount: settings.financial?.maxWithdrawalAmount === "" ? 0 : Number(settings.financial?.maxWithdrawalAmount ?? 0),
          gstRate: settings.financial?.gstRate === "" ? 0 : Number(settings.financial?.gstRate ?? 0),
        },
        dispatch: {
          ...settings.dispatch,
          bookingExpiryMinutes: settings.dispatch?.bookingExpiryMinutes === "" ? 3 : Number(settings.dispatch?.bookingExpiryMinutes ?? 3),
          maxSearchRadiusKm: settings.dispatch?.maxSearchRadiusKm === "" ? 15 : Number(settings.dispatch?.maxSearchRadiusKm ?? 15),
          autoCutEnabled: Boolean(settings.dispatch?.autoCutEnabled),
        },
      };

      const res = await fetch(`${BASE_URL}admin/settings`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const json = await res.json();

      if (json.status === "success") {
        showToast("Platform settings saved successfully!", "success");
        if (json.data) setSettings(json.data);
      } else {
        showToast(json.message || "Failed to update settings", "error");
      }
    } catch (err) {
      console.error("Save settings error:", err);
      showToast("Network error saving settings", "error");
    } finally {
      setSaving(false);
    }
  };

  // Handle Change Password
  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!passwordForm.oldPassword) {
      showToast("Please enter your current password", "error");
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      showToast("New password must be at least 6 characters", "error");
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showToast("New passwords do not match", "error");
      return;
    }

    setPasswordSaving(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${BASE_URL}admin/settings/password`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          oldPassword: passwordForm.oldPassword,
          newPassword: passwordForm.newPassword,
        }),
      });
      const json = await res.json();

      if (json.status === "success") {
        showToast("Password changed successfully!", "success");
        setPasswordForm({
          oldPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      } else {
        showToast(json.message || "Failed to change password", "error");
      }
    } catch (err) {
      console.error("Password change error:", err);
      showToast("Network error changing password", "error");
    } finally {
      setPasswordSaving(false);
    }
  };

  const formatUptime = (seconds) => {
    if (!seconds) return "Just started";
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hrs > 0) return `${hrs}h ${mins}m`;
    return `${mins} minutes`;
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] text-[#1C1917] flex flex-col font-sans">
      <Sidebar admin={admin} onLogout={handleLogout} />

      {/* Main Container with lg:ml-72 to avoid sidebar overlap */}
      <main className="lg:ml-72 min-h-screen flex flex-col flex-1 pb-16">
        {/* Toast Notification Banner */}
        {toast && (
          <div
            className={`fixed top-5 right-5 z-50 px-4 py-3 rounded-xl shadow-lg flex items-center space-x-3 text-xs font-bold transition-all ${
              toast.type === "success"
                ? "bg-stone-950 text-emerald-400 border border-emerald-500/30"
                : toast.type === "error"
                ? "bg-stone-950 text-rose-400 border border-rose-500/30"
                : "bg-stone-950 text-amber-300 border border-amber-500/30"
            }`}
          >
            <span className="w-2 h-2 rounded-full bg-current"></span>
            <span>{toast.message}</span>
          </div>
        )}

        {/* Sticky Top Header */}
        <header className="bg-white border-b border-stone-200 px-4 sm:px-6 lg:px-8 py-5 sticky top-0 z-30 shadow-xs">
          <div className="pt-16 lg:pt-0 flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
            <div>
              <div className="flex items-center space-x-2 text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1">
                <span>Admin</span>
                <span>/</span>
                <span className="text-stone-900 font-bold">Configuration</span>
              </div>
              <h1 className="text-2xl lg:text-3xl font-bold tracking-tight text-stone-900 flex items-center gap-3">
                <span>Platform Settings & Rules</span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200">
                  <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
                  <span>Control Console</span>
                </span>
              </h1>
              <p className="text-sm text-stone-500 mt-1">
                Manage global platform commission, buddy withdrawal limits, search timeouts, support numbers, and admin security.
              </p>
            </div>

            <div className="flex items-center space-x-3 self-start xl:self-auto">
              <button
                onClick={() => fetchSettings(true)}
                disabled={refreshing}
                className="inline-flex items-center px-3.5 py-2 text-xs font-bold rounded-xl bg-white border border-stone-300 text-stone-700 hover:bg-stone-50 transition-all shadow-xs disabled:opacity-50"
              >
                <svg
                  className={`w-4 h-4 mr-2 text-stone-500 ${refreshing ? "animate-spin" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                {refreshing ? "Reloading..." : "Reload"}
              </button>

              <button
                onClick={handleSaveSettings}
                disabled={saving || loading}
                className="inline-flex items-center px-4 py-2 text-xs font-bold rounded-xl bg-stone-900 text-white hover:bg-stone-800 transition-all shadow-xs disabled:opacity-50"
              >
                <svg
                  className={`w-4 h-4 mr-2 text-[#F8D346] ${saving ? "animate-spin" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                {saving ? "Saving Changes..." : "Save All Settings"}
              </button>
            </div>
          </div>
        </header>

        {/* Body Content */}
        {loading ? (
          <div className="px-4 sm:px-6 lg:px-8 pt-16 flex flex-col items-center justify-center min-h-[500px]">
            <div className="bg-white border border-stone-200 rounded-3xl p-10 max-w-md w-full shadow-sm text-center flex flex-col items-center space-y-4">
              <div className="relative flex items-center justify-center">
                <div className="w-16 h-16 rounded-full border-4 border-stone-200 border-t-[#F8D346] animate-spin"></div>
                <div className="absolute w-8 h-8 rounded-full bg-stone-900 flex items-center justify-center shadow-xs">
                  <svg className="w-4 h-4 text-[#F8D346]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                  </svg>
                </div>
              </div>

              <div className="space-y-1.5">
                <h3 className="text-base font-black text-stone-900 tracking-tight">
                  Loading Platform Settings...
                </h3>
                <p className="text-xs text-stone-500 max-w-xs mx-auto leading-relaxed">
                  Fetching live business parameters, financial rates, dispatch rules, and system status from server.
                </p>
              </div>

              <div className="w-full bg-stone-100 h-1.5 rounded-full overflow-hidden">
                <div className="bg-[#F8D346] h-full w-2/3 animate-pulse rounded-full"></div>
              </div>
            </div>
          </div>
        ) : (
          <div className="px-4 sm:px-6 lg:px-8 pt-6 space-y-6">
            {/* Syncing indicator if refreshing */}
            {refreshing && (
              <div className="bg-amber-50 border border-amber-200 text-amber-900 px-4 py-2.5 rounded-xl flex items-center justify-between text-xs font-semibold shadow-xs">
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-amber-600 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>Synchronizing live configuration with backend...</span>
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-200/60 px-2 py-0.5 rounded text-amber-800">
                  Refreshing
                </span>
              </div>
            )}

            {/* Navigation Segmented Tabs */}
            <div className="bg-white border border-stone-200 rounded-xl p-1.5 shadow-sm inline-flex flex-wrap gap-1">
            <button
              onClick={() => setActiveTab("financial")}
              className={`px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center space-x-2 ${
                activeTab === "financial"
                  ? "bg-stone-900 text-white shadow-xs"
                  : "text-stone-600 hover:text-stone-900 hover:bg-stone-50"
              }`}
            >
              <svg className="w-4 h-4 text-[#F8D346]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Financial & Commission Rules</span>
            </button>

            <button
              onClick={() => setActiveTab("dispatch")}
              className={`px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center space-x-2 ${
                activeTab === "dispatch"
                  ? "bg-stone-900 text-white shadow-xs"
                  : "text-stone-600 hover:text-stone-900 hover:bg-stone-50"
              }`}
            >
              <svg className="w-4 h-4 text-[#F8D346]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Dispatch & Booking Expiry</span>
            </button>

            <button
              onClick={() => setActiveTab("support")}
              className={`px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center space-x-2 ${
                activeTab === "support"
                  ? "bg-stone-900 text-white shadow-xs"
                  : "text-stone-600 hover:text-stone-900 hover:bg-stone-50"
              }`}
            >
              <svg className="w-4 h-4 text-[#F8D346]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <span>Support & Helpline</span>
            </button>

            <button
              onClick={() => setActiveTab("security")}
              className={`px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center space-x-2 ${
                activeTab === "security"
                  ? "bg-stone-900 text-white shadow-xs"
                  : "text-stone-600 hover:text-stone-900 hover:bg-stone-50"
              }`}
            >
              <svg className="w-4 h-4 text-[#F8D346]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <span>Admin Security & Health</span>
            </button>

            <button
              onClick={() => setActiveTab("appVersion")}
              className={`px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center space-x-2 ${
                activeTab === "appVersion"
                  ? "bg-stone-900 text-white shadow-xs"
                  : "text-stone-600 hover:text-stone-900 hover:bg-stone-50"
              }`}
            >
              <svg className="w-4 h-4 text-[#F8D346]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              <span>Mobile App Version</span>
            </button>
          </div>

          {/* TAB 1: FINANCIAL & COMMISSION */}
          {activeTab === "financial" && (
            <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm space-y-6">
              <div>
                <h3 className="text-base font-bold text-stone-900">
                  Business & Financial Parameters
                </h3>
                <p className="text-xs text-stone-500 mt-0.5">
                  Configure platform revenue commission cuts, minimum and maximum payout thresholds, and buddy referral incentives.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Commission % */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-stone-800">
                    Platform Commission Take Rate (%)
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={settings.financial.commissionRate}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          financial: {
                            ...settings.financial,
                            commissionRate: e.target.value,
                          },
                        })
                      }
                      className="w-full px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-xl text-sm font-semibold text-stone-900 focus:outline-none focus:ring-1 focus:ring-amber-400 focus:bg-white"
                    />
                    <span className="absolute right-3.5 top-2.5 text-xs font-bold text-stone-400">
                      %
                    </span>
                  </div>
                  <span className="text-[11px] text-stone-500">
                    Default commission cut automatically deducted on completed bookings.
                  </span>
                </div>

                {/* Max Cash Dues Cutoff Limit */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-stone-800">
                    Max Cash Dues Cutoff Limit (₹)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-2.5 text-xs font-bold text-stone-500">
                      ₹
                    </span>
                    <input
                      type="number"
                      min="0"
                      value={settings.financial.maxOutstandingCashDues ?? 500}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          financial: {
                            ...settings.financial,
                            maxOutstandingCashDues: e.target.value,
                          },
                        })
                      }
                      className="w-full pl-7 pr-3 py-2.5 bg-stone-50 border border-stone-200 rounded-xl text-sm font-semibold text-stone-900 focus:outline-none focus:ring-1 focus:ring-amber-400 focus:bg-white"
                    />
                  </div>
                  <span className="text-[11px] text-stone-500">
                    Duty auto-locks & dispatch stops when partner cash dues reach this amount.
                  </span>
                </div>

                {/* Referral Bonus */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-stone-800">
                    Buddy Referral Bonus Reward (₹)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-2.5 text-xs font-bold text-stone-500">
                      ₹
                    </span>
                    <input
                      type="number"
                      min="0"
                      value={settings.financial.referralBonus}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          financial: {
                            ...settings.financial,
                            referralBonus: e.target.value,
                          },
                        })
                      }
                      className="w-full pl-7 pr-3 py-2.5 bg-stone-50 border border-stone-200 rounded-xl text-sm font-semibold text-stone-900 focus:outline-none focus:ring-1 focus:ring-amber-400 focus:bg-white"
                    />
                  </div>
                  <span className="text-[11px] text-stone-500">
                    Credited to provider's wallet when their referred buddy completes onboarding.
                  </span>
                </div>

                {/* Min Withdrawal Amount */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-stone-800">
                    Minimum Wallet Withdrawal Threshold (₹)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-2.5 text-xs font-bold text-stone-500">
                      ₹
                    </span>
                    <input
                      type="number"
                      min="0"
                      value={settings.financial.minWithdrawalAmount}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          financial: {
                            ...settings.financial,
                            minWithdrawalAmount: e.target.value,
                          },
                        })
                      }
                      className="w-full pl-7 pr-3 py-2.5 bg-stone-50 border border-stone-200 rounded-xl text-sm font-semibold text-stone-900 focus:outline-none focus:ring-1 focus:ring-amber-400 focus:bg-white"
                    />
                  </div>
                  <span className="text-[11px] text-stone-500">
                    Buddies cannot request bank withdrawals below this balance.
                  </span>
                </div>

                {/* Max Single Withdrawal */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-stone-800">
                    Maximum Single Payout Limit (₹)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-2.5 text-xs font-bold text-stone-500">
                      ₹
                    </span>
                    <input
                      type="number"
                      min="0"
                      value={settings.financial.maxWithdrawalAmount}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          financial: {
                            ...settings.financial,
                            maxWithdrawalAmount: e.target.value,
                          },
                        })
                      }
                      className="w-full pl-7 pr-3 py-2.5 bg-stone-50 border border-stone-200 rounded-xl text-sm font-semibold text-stone-900 focus:outline-none focus:ring-1 focus:ring-amber-400 focus:bg-white"
                    />
                  </div>
                  <span className="text-[11px] text-stone-500">
                    Maximum single transaction limit for automated bank transfers.
                  </span>
                </div>

                {/* GST Rate */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-stone-800">
                    Applicable Tax / GST Rate (%)
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min="0"
                      value={settings.financial.gstRate}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          financial: {
                            ...settings.financial,
                            gstRate: e.target.value,
                          },
                        })
                      }
                      className="w-full px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-xl text-sm font-semibold text-stone-900 focus:outline-none focus:ring-1 focus:ring-amber-400 focus:bg-white"
                    />
                    <span className="absolute right-3.5 top-2.5 text-xs font-bold text-stone-400">
                      %
                    </span>
                  </div>
                  <span className="text-[11px] text-stone-500">
                    GST applied on platform commission invoices.
                  </span>
                </div>
              </div>

              {/* In-Card Save Button */}
              <div className="pt-4 border-t border-stone-100 flex justify-end">
                <button
                  type="button"
                  onClick={handleSaveSettings}
                  disabled={saving || loading}
                  className="inline-flex items-center px-5 py-2.5 text-xs font-bold rounded-xl bg-stone-900 text-white hover:bg-stone-800 transition-all shadow-sm disabled:opacity-50"
                >
                  <svg
                    className={`w-3.5 h-3.5 mr-2 text-[#F8D346] ${saving ? "animate-spin" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {saving ? "Saving Changes..." : "Save Financial Rules"}
                </button>
              </div>
            </div>
          )}

          {/* TAB 2: DISPATCH & BOOKING RULES */}
          {activeTab === "dispatch" && (
            <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm space-y-6">
              <div>
                <h3 className="text-base font-bold text-stone-900">
                  Dispatch Engine & Booking Expiry Rules
                </h3>
                <p className="text-xs text-stone-500 mt-0.5">
                  Configure search radar sensitivity, radius limitations, and automated cancellation timeouts.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Search Expiry Timeout */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-stone-800">
                    Unaccepted Search Auto-Cut Timeout (Minutes)
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min="1"
                      max="30"
                      value={settings.dispatch.bookingExpiryMinutes}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          dispatch: {
                            ...settings.dispatch,
                            bookingExpiryMinutes: e.target.value,
                          },
                        })
                      }
                      className="w-full px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-xl text-sm font-semibold text-stone-900 focus:outline-none focus:ring-1 focus:ring-amber-400 focus:bg-white"
                    />
                    <span className="absolute right-3.5 top-2.5 text-xs font-bold text-stone-400">
                      mins
                    </span>
                  </div>
                  <span className="text-[11px] text-stone-500">
                    If no buddy accepts a booking within this window, the system automatically cancels it and emits a real-time refund.
                  </span>
                </div>

                {/* Search Radius */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-stone-800">
                    Maximum Buddy Broadcast Radius (KM)
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={settings.dispatch.maxSearchRadiusKm}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          dispatch: {
                            ...settings.dispatch,
                            maxSearchRadiusKm: e.target.value,
                          },
                        })
                      }
                      className="w-full px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-xl text-sm font-semibold text-stone-900 focus:outline-none focus:ring-1 focus:ring-amber-400 focus:bg-white"
                    />
                    <span className="absolute right-3.5 top-2.5 text-xs font-bold text-stone-400">
                      km
                    </span>
                  </div>
                  <span className="text-[11px] text-stone-500">
                    Geographical radius around the customer location to ping online buddies.
                  </span>
                </div>

                {/* Auto-cut Toggle */}
                <div className="p-4 rounded-xl bg-stone-50 border border-stone-200 flex items-center justify-between col-span-1 md:col-span-2">
                  <div>
                    <h4 className="text-xs font-bold text-stone-900">
                      Enable Automated 3-Minute Background Expiry Watcher
                    </h4>
                    <p className="text-[11px] text-stone-500 mt-0.5">
                      Periodically cleans stuck SEARCHING bookings older than the timeout window.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setSettings({
                        ...settings,
                        dispatch: {
                          ...settings.dispatch,
                          autoCutEnabled: !settings.dispatch.autoCutEnabled,
                        },
                      })
                    }
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                      settings.dispatch.autoCutEnabled ? "bg-stone-900" : "bg-stone-300"
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
                        settings.dispatch.autoCutEnabled ? "translate-x-5" : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* In-Card Save Button */}
              <div className="pt-4 border-t border-stone-100 flex justify-end">
                <button
                  type="button"
                  onClick={handleSaveSettings}
                  disabled={saving || loading}
                  className="inline-flex items-center px-5 py-2.5 text-xs font-bold rounded-xl bg-stone-900 text-white hover:bg-stone-800 transition-all shadow-sm disabled:opacity-50"
                >
                  <svg
                    className={`w-3.5 h-3.5 mr-2 text-[#F8D346] ${saving ? "animate-spin" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {saving ? "Saving Changes..." : "Save Dispatch Rules"}
                </button>
              </div>
            </div>
          )}

          {/* TAB 3: SUPPORT & HELPLINE */}
          {activeTab === "support" && (
            <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm space-y-6">
              <div>
                <h3 className="text-base font-bold text-stone-900">
                  Customer Support & Helpdesk Information
                </h3>
                <p className="text-xs text-stone-500 mt-0.5">
                  These contact details are displayed inside customer & buddy mobile apps on the Help & Support screen.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-stone-800">
                    Official Customer Care Phone
                  </label>
                  <input
                    type="text"
                    value={settings.support.supportPhone}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        support: { ...settings.support, supportPhone: e.target.value },
                      })
                    }
                    className="w-full px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-xl text-sm font-semibold text-stone-900 focus:outline-none focus:ring-1 focus:ring-amber-400 focus:bg-white"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-stone-800">
                    WhatsApp Helpline Number
                  </label>
                  <input
                    type="text"
                    value={settings.support.supportWhatsapp}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        support: { ...settings.support, supportWhatsapp: e.target.value },
                      })
                    }
                    className="w-full px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-xl text-sm font-semibold text-stone-900 focus:outline-none focus:ring-1 focus:ring-amber-400 focus:bg-white"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-stone-800">
                    Support Email Address
                  </label>
                  <input
                    type="email"
                    value={settings.support.supportEmail}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        support: { ...settings.support, supportEmail: e.target.value },
                      })
                    }
                    className="w-full px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-xl text-sm font-semibold text-stone-900 focus:outline-none focus:ring-1 focus:ring-amber-400 focus:bg-white"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-stone-800">
                    Operating / Helpdesk Working Hours
                  </label>
                  <input
                    type="text"
                    value={settings.support.operatingHours}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        support: { ...settings.support, operatingHours: e.target.value },
                      })
                    }
                    className="w-full px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-xl text-sm font-semibold text-stone-900 focus:outline-none focus:ring-1 focus:ring-amber-400 focus:bg-white"
                  />
                  <span className="text-[11px] text-stone-500">
                    Displayed to customers for live phone assistance.
                  </span>
                </div>
              </div>

              {/* In-Card Save Button */}
              <div className="pt-4 border-t border-stone-100 flex justify-end">
                <button
                  type="button"
                  onClick={handleSaveSettings}
                  disabled={saving || loading}
                  className="inline-flex items-center px-5 py-2.5 text-xs font-bold rounded-xl bg-stone-900 text-white hover:bg-stone-800 transition-all shadow-sm disabled:opacity-50"
                >
                  <svg
                    className={`w-3.5 h-3.5 mr-2 text-[#F8D346] ${saving ? "animate-spin" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {saving ? "Saving Changes..." : "Save Support Details"}
                </button>
              </div>
            </div>
          )}

          {/* TAB 4: SECURITY & SYSTEM HEALTH */}
          {activeTab === "security" && (
            <div className="space-y-6">
              {/* Server Diagnostics & Health */}
              <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm">
                <h3 className="text-base font-bold text-stone-900 mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                  <span>System Diagnostics & Service Health</span>
                </h3>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 text-xs">
                  <div className="p-3 bg-stone-50 rounded-xl border border-stone-200">
                    <span className="text-stone-500 block">Database (MongoDB)</span>
                    <span className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700 mt-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                      {diagnostics.database}
                    </span>
                  </div>

                  <div className="p-3 bg-stone-50 rounded-xl border border-stone-200">
                    <span className="text-stone-500 block">Cache (Redis)</span>
                    <span className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700 mt-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                      {diagnostics.redis}
                    </span>
                  </div>

                  <div className="p-3 bg-stone-50 rounded-xl border border-stone-200">
                    <span className="text-stone-500 block">Node Runtime</span>
                    <span className="font-mono font-bold text-stone-900 mt-1 block">
                      {diagnostics.nodeVersion}
                    </span>
                  </div>

                  <div className="p-3 bg-stone-50 rounded-xl border border-stone-200">
                    <span className="text-stone-500 block">Backend Uptime</span>
                    <span className="font-mono font-bold text-stone-900 mt-1 block">
                      {formatUptime(diagnostics.uptimeSeconds)}
                    </span>
                  </div>

                  <div className="p-3 bg-stone-50 rounded-xl border border-stone-200">
                    <span className="text-stone-500 block">Heap Memory</span>
                    <span className="font-mono font-bold text-stone-900 mt-1 block">
                      {diagnostics.memoryUsageMB} MB
                    </span>
                  </div>
                </div>
              </div>

              {/* Admin Profile & Password Change */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Admin Profile Info */}
                <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm space-y-4">
                  <h3 className="text-base font-bold text-stone-900">
                    Super Administrator Credentials
                  </h3>
                  <div className="space-y-3 text-xs">
                    <div className="flex justify-between py-2 border-b border-stone-100">
                      <span className="text-stone-500">Administrator Name:</span>
                      <span className="font-bold text-stone-900">
                        {adminProfile?.name || admin?.name || "Abhi"}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-stone-100">
                      <span className="text-stone-500">Registered Email:</span>
                      <span className="font-mono text-stone-900">
                        {adminProfile?.email || admin?.email || "abhi@gmail.com"}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-stone-100">
                      <span className="text-stone-500">Registered Phone:</span>
                      <span className="font-mono text-stone-900">
                        {adminProfile?.phone || "8887838118"}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-stone-100">
                      <span className="text-stone-500">Access Level:</span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-black bg-[#F8D346]/40 text-stone-950 border border-[#F5C518]">
                        SUPER_ADMIN
                      </span>
                    </div>
                  </div>
                </div>

                {/* Password Change Form */}
                <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm">
                  <h3 className="text-base font-bold text-stone-900 mb-1">
                    Update Admin Password
                  </h3>
                  <p className="text-xs text-stone-500 mb-4">
                    Change your administrative login credentials securely.
                  </p>

                  <form onSubmit={handleChangePassword} className="space-y-3.5">
                    <div>
                      <label className="block text-xs font-bold text-stone-700 mb-1">
                        Current Password
                      </label>
                      <input
                        type="password"
                        value={passwordForm.oldPassword}
                        onChange={(e) =>
                          setPasswordForm({
                            ...passwordForm,
                            oldPassword: e.target.value,
                          })
                        }
                        placeholder="••••••••"
                        className="w-full px-3 py-2 bg-stone-50 border border-stone-200 rounded-xl text-xs focus:outline-none focus:ring-1 focus:ring-amber-400 focus:bg-white"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-stone-700 mb-1">
                        New Password (Min 6 chars)
                      </label>
                      <input
                        type="password"
                        value={passwordForm.newPassword}
                        onChange={(e) =>
                          setPasswordForm({
                            ...passwordForm,
                            newPassword: e.target.value,
                          })
                        }
                        placeholder="••••••••"
                        className="w-full px-3 py-2 bg-stone-50 border border-stone-200 rounded-xl text-xs focus:outline-none focus:ring-1 focus:ring-amber-400 focus:bg-white"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-stone-700 mb-1">
                        Confirm New Password
                      </label>
                      <input
                        type="password"
                        value={passwordForm.confirmPassword}
                        onChange={(e) =>
                          setPasswordForm({
                            ...passwordForm,
                            confirmPassword: e.target.value,
                          })
                        }
                        placeholder="••••••••"
                        className="w-full px-3 py-2 bg-stone-50 border border-stone-200 rounded-xl text-xs focus:outline-none focus:ring-1 focus:ring-amber-400 focus:bg-white"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={passwordSaving}
                      className="w-full py-2.5 text-xs font-bold rounded-xl bg-stone-900 text-white hover:bg-stone-800 transition-all disabled:opacity-50 mt-2"
                    >
                      {passwordSaving ? "Updating Password..." : "Change Password"}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* TAB 5: MOBILE APP VERSION CONTROL (USER & PROVIDER) */}
          {activeTab === "appVersion" && (
            <div className="space-y-6">
              {/* Header Card */}
              <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm flex items-center justify-between">
                <div>
                  <h3 className="text-base font-bold text-stone-900 flex items-center gap-2">
                    <span>🚀</span>
                    <span>Mobile App Versions & Play Store Updates</span>
                  </h3>
                  <p className="text-xs text-stone-500 mt-0.5">
                    Manage current releases, enforce mandatory strict updates, and configure Play Store links for both User and Partner/Provider apps.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleSaveSettings}
                  disabled={saving}
                  className="bg-stone-900 hover:bg-black text-white px-5 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center space-x-2 shadow-xs disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save App Versions"}
                </button>
              </div>

              {/* Grid with 2 distinct cards: User App & Provider App */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* 1. CUSTOMER / USER APP CARD */}
                <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm space-y-5">
                  <div className="flex items-center justify-between border-b border-stone-100 pb-3">
                    <div className="flex items-center space-x-2">
                      <span className="text-xl">📱</span>
                      <div>
                        <h4 className="text-sm font-bold text-stone-900">User App (Customer)</h4>
                        <span className="text-[11px] font-mono text-stone-400">com.inbuser</span>
                      </div>
                    </div>
                    <span className="px-2.5 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg text-[10px] font-bold">
                      Customer Release
                    </span>
                  </div>

                  {/* Latest Version Input */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-stone-800">
                      Latest Version (SemVer)
                    </label>
                    <input
                      type="text"
                      value={settings.appVersion?.latestVersion || ""}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          appVersion: {
                            ...settings.appVersion,
                            latestVersion: e.target.value,
                          },
                        })
                      }
                      placeholder="e.g. 1.0.2"
                      className="w-full text-xs font-mono bg-stone-50 border border-stone-200 rounded-xl px-3.5 py-2.5 text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:bg-white"
                    />
                    <p className="text-[11px] text-stone-400">
                      Current released version on Google Play Store for customers.
                    </p>
                  </div>

                  {/* Force Update Toggle */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-stone-800">
                      Strict Update Policy
                    </label>
                    <div className="bg-stone-50 border border-stone-200 rounded-xl p-3 flex items-center justify-between">
                      <div>
                        <span className="text-xs font-bold text-stone-900 block">
                          Strict Force Update
                        </span>
                        <span className="text-[11px] text-stone-500">
                          {settings.appVersion?.forceUpdate
                            ? "🔒 Users CANNOT use app without updating"
                            : "Users can choose 'Remind Me Later'"}
                        </span>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={Boolean(settings.appVersion?.forceUpdate)}
                          onChange={(e) =>
                            setSettings({
                              ...settings,
                              appVersion: {
                                ...settings.appVersion,
                                forceUpdate: e.target.checked,
                              },
                            })
                          }
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-stone-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-stone-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FFCC0A]"></div>
                      </label>
                    </div>
                  </div>

                  {/* Store URL */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-stone-800">
                      User App Play Store URL
                    </label>
                    <input
                      type="text"
                      value={settings.appVersion?.storeUrl || "https://play.google.com/store/apps/details?id=com.inbuser&hl=en_IN"}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          appVersion: {
                            ...settings.appVersion,
                            storeUrl: e.target.value,
                          },
                        })
                      }
                      className="w-full text-xs font-mono bg-stone-50 border border-stone-200 rounded-xl px-3.5 py-2.5 text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:bg-white"
                    />
                  </div>
                </div>

                {/* 2. PARTNER / PROVIDER APP CARD */}
                <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm space-y-5">
                  <div className="flex items-center justify-between border-b border-stone-100 pb-3">
                    <div className="flex items-center space-x-2">
                      <span className="text-xl">🛠️</span>
                      <div>
                        <h4 className="text-sm font-bold text-stone-900">Partner App (Provider)</h4>
                        <span className="text-[11px] font-mono text-stone-400">com.aloneking6798.inbprovider</span>
                      </div>
                    </div>
                    <span className="px-2.5 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg text-[10px] font-bold">
                      Provider Release
                    </span>
                  </div>

                  {/* Latest Version Input */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-stone-800">
                      Latest Version (SemVer)
                    </label>
                    <input
                      type="text"
                      value={settings.providerAppVersion?.latestVersion || ""}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          providerAppVersion: {
                            ...settings.providerAppVersion,
                            latestVersion: e.target.value,
                          },
                        })
                      }
                      placeholder="e.g. 1.0.0"
                      className="w-full text-xs font-mono bg-stone-50 border border-stone-200 rounded-xl px-3.5 py-2.5 text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:bg-white"
                    />
                    <p className="text-[11px] text-stone-400">
                      When higher than provider's installed version, update drawer will slide up on launch.
                    </p>
                  </div>

                  {/* Force Update Toggle */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-stone-800">
                      Strict Update Policy
                    </label>
                    <div className="bg-stone-50 border border-stone-200 rounded-xl p-3 flex items-center justify-between">
                      <div>
                        <span className="text-xs font-bold text-stone-900 block">
                          Strict Force Update
                        </span>
                        <span className="text-[11px] text-stone-500">
                          {settings.providerAppVersion?.forceUpdate
                            ? "🔒 Providers CANNOT use app without updating"
                            : "Providers can choose 'Remind Me Later'"}
                        </span>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={Boolean(settings.providerAppVersion?.forceUpdate)}
                          onChange={(e) =>
                            setSettings({
                              ...settings,
                              providerAppVersion: {
                                ...settings.providerAppVersion,
                                forceUpdate: e.target.checked,
                              },
                            })
                          }
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-stone-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-stone-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FFCC0A]"></div>
                      </label>
                    </div>
                  </div>

                  {/* Store URL */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-stone-800">
                      Partner App Play Store URL
                    </label>
                    <input
                      type="text"
                      value={settings.providerAppVersion?.storeUrl || "https://play.google.com/store/apps/details?id=com.aloneking6798.inbprovider"}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          providerAppVersion: {
                            ...settings.providerAppVersion,
                            storeUrl: e.target.value,
                          },
                        })
                      }
                      className="w-full text-xs font-mono bg-stone-50 border border-stone-200 rounded-xl px-3.5 py-2.5 text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:bg-white"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </main>
    </div>
  );
};

export default Settings;
